import * as PIXI from 'pixi.js';
import { globalEventBus, GameEvents } from '../core/EventBus.js';

/**
 * UI管理器
 * 管理游戏界面元素
 */
export class UIManager {
    constructor(app, config) {
        this.app = app;
        this.config = config;
        this.events = globalEventBus;

        // UI容器
        this.container = new PIXI.Container();
        this.app.stage.addChild(this.container);

        // UI元素引用
        this.elements = {
            timerText: null,
            scoreText: null,
            progressText: null,
            messageOverlay: null
        };

        this.init();
        this.bindEvents();
    }

    /**
     * 初始化UI元素
     */
    init() {
        // 暂时隐藏UI显示
        // this.createTimerDisplay();
        // this.createScoreDisplay();
        // this.createProgressDisplay();
    }

    /**
     * 创建计时器显示
     */
    createTimerDisplay() {
        const timerText = new PIXI.Text({
            text: '00:00',
            style: {
                fontSize: 24,
                fontWeight: 'bold',
                fill: 0xffffff,
                fontFamily: 'Arial, sans-serif'
            }
        });

        timerText.x = 20;
        timerText.y = 15;

        this.container.addChild(timerText);
        this.elements.timerText = timerText;
    }

    /**
     * 创建分数显示
     */
    createScoreDisplay() {
        const scoreText = new PIXI.Text({
            text: 'Score: 0',
            style: {
                fontSize: 20,
                fontWeight: 'bold',
                fill: 0xf1c40f,
                fontFamily: 'Arial, sans-serif'
            }
        });

        scoreText.x = 150;
        scoreText.y = 18;

        this.container.addChild(scoreText);
        this.elements.scoreText = scoreText;
    }

    /**
     * 创建进度显示
     */
    createProgressDisplay() {
        const progressText = new PIXI.Text({
            text: '0%',
            style: {
                fontSize: 20,
                fontWeight: 'bold',
                fill: 0x2ecc71,
                fontFamily: 'Arial, sans-serif'
            }
        });

        progressText.anchor.set(1, 0);
        progressText.x = this.app.screen.width - 20;
        progressText.y = 18;

        this.container.addChild(progressText);
        this.elements.progressText = progressText;
    }

    /**
     * 绑定事件
     */
    bindEvents() {
        // 计时器更新
        this.events.on(GameEvents.TIMER_TICK, (data) => {
            this.updateTimer(data.elapsed);
        });

        // 分数更新
        this.events.on(GameEvents.SCORE_CHANGED, (stats) => {
            this.updateScore(stats.score);
        });

        // 拼图块吸附
        this.events.on(GameEvents.PIECE_SNAP, () => {
            // 可以在这里添加吸附动画效果
        });

        // 游戏完成
        this.events.on(GameEvents.GAME_COMPLETE, () => {
            // 已使用 DOM 弹窗，不再显示 PIXI 文本
            // this.showCompletionMessage();
        });

        // 游戏重置
        this.events.on(GameEvents.GAME_RESET, () => {
            this.reset();
        });
    }

    /**
     * 更新计时器显示
     * @param {number} elapsed 已过时间（秒）
     */
    updateTimer(elapsed) {
        if (!this.elements.timerText) return;
        const minutes = Math.floor(elapsed / 60);
        const seconds = Math.floor(elapsed % 60);
        const timeStr = `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
        this.elements.timerText.text = timeStr;
    }

    /**
     * 更新分数显示
     * @param {number} score 当前分数
     */
    updateScore(score) {
        if (!this.elements.scoreText) return;
        this.elements.scoreText.text = `Score: ${score.toLocaleString()}`;
    }

    /**
     * 更新进度显示
     * @param {number} progress 进度百分比 0-100
     */
    updateProgress(progress) {
        if (!this.elements.progressText) return;
        this.elements.progressText.text = `${progress}%`;

        // 根据进度改变颜色
        if (progress >= 100) {
            this.elements.progressText.style.fill = 0x27ae60;
        } else if (progress >= 50) {
            this.elements.progressText.style.fill = 0xf39c12;
        } else {
            this.elements.progressText.style.fill = 0x2ecc71;
        }
    }

    /**
     * 显示完成消息
     */
    showCompletionMessage() {
        // 创建半透明遮罩
        const overlay = new PIXI.Graphics();
        overlay.fill({ color: 0x000000, alpha: 0.7 });
        overlay.rect(0, 0, this.app.screen.width, this.app.screen.height);

        // 创建消息文本
        const messageText = new PIXI.Text({
            text: '🎉 Puzzle Complete! 🎉',
            style: {
                fontSize: 48,
                fontWeight: 'bold',
                fill: 0xffffff,
                fontFamily: 'Arial, sans-serif',
                dropShadow: true,
                dropShadowColor: 0x000000,
                dropShadowDistance: 3
            }
        });

        messageText.anchor.set(0.5);
        messageText.x = this.app.screen.width / 2;
        messageText.y = this.app.screen.height / 2;

        // 创建容器
        const container = new PIXI.Container();
        container.addChild(overlay);
        container.addChild(messageText);

        // 添加点击关闭功能
        container.eventMode = 'static';
        container.cursor = 'pointer';
        container.on('pointerdown', () => {
            this.container.removeChild(container);
            container.destroy({ children: true });
            this.elements.messageOverlay = null;
        });

        this.container.addChild(container);
        this.elements.messageOverlay = container;

        // 添加入场动画
        messageText.scale.set(0);
        this.animateScale(messageText, 0, 1, 500);
    }

    /**
     * 显示提示消息
     * @param {string} message 消息内容
     * @param {number} duration 显示时长（毫秒）
     */
    showToast(message, duration = 2000) {
        const toast = new PIXI.Text({
            text: message,
            style: {
                fontSize: 20,
                fill: 0xffffff,
                fontFamily: 'Arial, sans-serif'
            }
        });

        // 创建背景
        const bg = new PIXI.Graphics();
        const padding = 15;
        bg.fill({ color: 0x333333, alpha: 0.9 });
        bg.roundRect(0, 0, toast.width + padding * 2, toast.height + padding * 2, 8);

        const container = new PIXI.Container();
        container.addChild(bg);
        container.addChild(toast);

        toast.x = padding;
        toast.y = padding;

        container.x = (this.app.screen.width - container.width) / 2;
        container.y = this.app.screen.height - 100;

        this.container.addChild(container);

        // 自动消失
        setTimeout(() => {
            this.animateAlpha(container, 1, 0, 300, () => {
                this.container.removeChild(container);
                container.destroy({ children: true });
            });
        }, duration);
    }

    /**
     * 缩放动画
     * @param {PIXI.DisplayObject} target 目标对象
     * @param {number} from 起始值
     * @param {number} to 结束值
     * @param {number} duration 持续时间
     */
    animateScale(target, from, to, duration) {
        target.scale.set(from);
        const startTime = Date.now();

        const animate = () => {
            const elapsed = Date.now() - startTime;
            const progress = Math.min(elapsed / duration, 1);

            // 弹性缓动
            const eased = 1 - Math.pow(1 - progress, 3);
            const scale = from + (to - from) * eased;

            target.scale.set(scale);

            if (progress < 1) {
                requestAnimationFrame(animate);
            }
        };

        animate();
    }

    /**
     * 透明度动画
     * @param {PIXI.DisplayObject} target 目标对象
     * @param {number} from 起始值
     * @param {number} to 结束值
     * @param {number} duration 持续时间
     * @param {Function} onComplete 完成回调
     */
    animateAlpha(target, from, to, duration, onComplete = null) {
        target.alpha = from;
        const startTime = Date.now();

        const animate = () => {
            const elapsed = Date.now() - startTime;
            const progress = Math.min(elapsed / duration, 1);

            target.alpha = from + (to - from) * progress;

            if (progress < 1) {
                requestAnimationFrame(animate);
            } else if (onComplete) {
                onComplete();
            }
        };

        animate();
    }

    /**
     * 重置UI
     */
    reset() {
        this.updateTimer(0);
        this.updateScore(0);
        this.updateProgress(0);

        if (this.elements.messageOverlay) {
            this.container.removeChild(this.elements.messageOverlay);
            this.elements.messageOverlay.destroy({ children: true });
            this.elements.messageOverlay = null;
        }
    }

    /**
     * 处理窗口大小变化
     */
    onResize() {
        // 更新进度文本位置
        if (this.elements.progressText) {
            this.elements.progressText.x = this.app.screen.width - 20;
        }
    }

    /**
     * 销毁
     */
    destroy() {
        this.container.destroy({ children: true });
    }
}

export default UIManager;
